import usePrivateAxios from "@/common/api/usePrivateAxios"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import GoBackButton from "@/common/components/GoBackButton"
import MaskedTextField from "@/common/components/MaskedTextField"
import SelectController from "@/common/components/SelectController"
import TextField from "@/common/components/TextField"
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories"
import { useEquipmentTiers } from "@/common/hooks/queries/useEquipmentTiers"
import { useProducts } from "@/common/hooks/queries/useProducts"
import useDebounce from "@/common/hooks/useDebounce"
import useUserPermissions from "@/common/hooks/useUserPermissions"
import { Mapper } from "@/common/services/mapper"
import { EquipmentTierData } from "@/common/types/equipmentTier"
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories"
import { ProductData } from "@/features/products/types/product"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { EquipmentModelValidationSchema, createSchema } from "../../schemas/create.schema"

const Create = () => {
  const navigate = useNavigate()
  const privateAxios = usePrivateAxios();
  const queryClient = useQueryClient();

  const [serverError, setServerError] = useState('');
  const [productSearch, setProductSearch] = useDebounce('');
  const [equipmentCategorySearch, setEquipmentCategorySearch] = useDebounce('');
  const [searchEquipmentTier, setSearchEquipmentTier] = useDebounce('');

  const { data: equipmentTierOptions } = useEquipmentTiers({
    filters: { search: searchEquipmentTier },
    mapper: Mapper.mapToOptions<EquipmentTierData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const { mutate: createEquipmentModel, isPending: createEquipmentModelIsPending } = useMutation({
    mutationKey: ['createEquipmentModel'],
    mutationFn: async (form: EquipmentModelValidationSchema) => {
      const equipment_model = {
        ...form,
        category_id: form.category_id?.value,
        equipment_tier_id: form.equipment_tier?.value || null,
        applicable_product_ids: form.applicable_product_ids?.map((product) => product.value) || []
      };

      delete equipment_model.equipment_tier;

      return privateAxios.post('/equipment_models', {
        equipment_model
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipmentModels'] });
      navigate('/equipment-models');
    },
    onError: () => {
      setServerError('generic');
    }
  });

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: equipmentCategorySearch },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: productOptions } = useProducts({
    filters: { search: productSearch },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateEquipmentModelPermission = !isUserPermissionsPending ? hasUserPermission('Admin::EquipmentModels::Create') : true

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<EquipmentModelValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const onSubmit: SubmitHandler<EquipmentModelValidationSchema> = (data) => createEquipmentModel(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateEquipmentModelPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton
              fallback={`/equipment-models`}
              searchLastRouteFromFallback
            />
            <p className="text-white-400 font-normal">Modelos de equipamento</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>Novo equipamento</h2>
          </div>
          <div className="flex gap-4">
            <Link to='/equipment-models'><Button variant='outline-error'>Cancelar</Button></Link>
            <Button
              name="submit"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={createEquipmentModelIsPending}
              isLoading={createEquipmentModelIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError == 'generic' && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <p className="text-lg font-bold">Modelo de equipamento</p>
          <div className="flex gap-2">
            <SelectController
              control={control}
              markAsRequired
              name="category_id"
              label="Categoria"
              onInputChange={setEquipmentCategorySearch}
              options={equipmentCategoryOptions || []}
              errorMessage={errors?.category_id?.message}
              className="w-1/2"
              dataTestid="category_id_selector"
              isDisabled={createEquipmentModelIsPending}
            />
            <MaskedTextField
              label="Ano"
              markAsRequired
              mask='0000'
              className="w-1/2"
              data-testid="year_input"
              errorMessage={errors?.year?.message || undefined}
              {...register('year')}
              disabled={createEquipmentModelIsPending}
            />
          </div>
          <div className="flex gap-2">
            <TextField
              label="Marca"
              markAsRequired
              data-testid="brand_input"
              className="w-1/2"
              errorMessage={errors?.brand?.message || undefined}
              {...register('brand')}
              disabled={createEquipmentModelIsPending}
            />
            <TextField
              label="Modelo"
              markAsRequired
              className="w-1/2"
              data-testid="display_name_input"
              errorMessage={errors?.display_name?.message || undefined}
              {...register('display_name')}
              disabled={createEquipmentModelIsPending}
            />
          </div>

          <SelectController
            control={control}
            name="equipment_tier"
            label="Porte"
            options={equipmentTierOptions || []}
            onInputChange={setSearchEquipmentTier}
            isClearable
            errorMessage={errors?.equipment_tier?.message}
            isDisabled={createEquipmentModelIsPending}
          />

          <SelectController
            control={control}
            name="applicable_product_ids"
            dataTestid="applicable_product_ids_selector"
            label="Produtos e serviços aplicáveis"
            isMulti
            onInputChange={(e) => setProductSearch(e)}
            options={productOptions || []}
            isDisabled={createEquipmentModelIsPending}
          />
        </Box>
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
